import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Factory, Users, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';

const EMPTY = { code: '', name: '', lead_time_days: 1, sort_order: 0, notes: '', active: true, operator_employee_ids: [], hourly_rate: '' };

export default function WorkCenters() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [list, setList] = useState([]);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [opSearch, setOpSearch] = useState('');

  const load = useCallback(() => { mfgApi.workCenters().then(setList).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { mfgApi.workCenterPeople().then((d) => setPeople(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const openNew = () => { setEditId(null); setOpSearch(''); setForm({ ...EMPTY, sort_order: (list.length + 1) * 10 }); setOpen(true); };
  const openEdit = (w) => { setEditId(w.id); setOpSearch(''); setForm({ ...EMPTY, ...w, name: w.name ?? '', notes: w.notes ?? '', operator_employee_ids: w.operator_employee_ids || [], hourly_rate: w.hourly_rate ?? '' }); setOpen(true); };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const toggleOperator = (id) => setForm((p) => {
    const has = (p.operator_employee_ids || []).includes(id);
    return { ...p, operator_employee_ids: has ? p.operator_employee_ids.filter((x) => x !== id) : [...(p.operator_employee_ids || []), id] };
  });
  const peopleName = (id) => people.find((e) => e.id === id)?.name || '';
  const filteredPeople = useMemo(() => {
    const q = opSearch.trim().toLowerCase();
    if (!q) return people;
    return people.filter((e) => (e.name || '').toLowerCase().includes(q) || (e.department_name || '').toLowerCase().includes(q));
  }, [people, opSearch]);

  const save = async () => {
    if (!String(form.code).trim()) { toast.error(t('mfg.wc.code')); return; }
    setBusy(true);
    try {
      const body = {
        code: String(form.code).trim(), name: form.name || null,
        lead_time_days: Math.max(0, Number(form.lead_time_days) || 0),
        sort_order: Number(form.sort_order) || 0, notes: form.notes || null, active: form.active !== false,
        operator_employee_ids: form.operator_employee_ids || [],
        hourly_rate: form.hourly_rate === '' || form.hourly_rate == null ? null : Number(form.hourly_rate),
      };
      if (editId) await mfgApi.updateWorkCenter(editId, body);
      else await mfgApi.createWorkCenter(body);
      toast.success(t('mfg.saved')); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };
  const del = async (w) => {
    if (!window.confirm(t('mfg.confirmDelete', { name: w.code }))) return;
    try { await mfgApi.deleteWorkCenter(w.id); toast.success(t('mfg.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };

  return (
    <div className="space-y-5" data-testid="mfg-work-centers-master">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.wc.title')}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{t('mfg.wc.desc')}</p>
        </div>
        {canWrite && (
          <Button data-testid="wc-new-btn" onClick={openNew} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.new')}
          </Button>
        )}
      </div>

      <DataTable
        dataTestId="wc-table"
        rows={list}
        rowKey={(w) => w.id}
        rowTestId={(w) => `wc-row-${w.id}`}
        empty={{ icon: <Factory size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.wc.empty'), testid: 'wc-empty' }}
        columns={[
          { key: 'code', primary: true, header: t('mfg.wc.code'), cell: (w) => <span className="font-mono font-medium text-zinc-800">{w.code}</span> },
          { key: 'name', header: t('mfg.wc.name'), cell: (w) => <span className="text-zinc-700">{w.name || '—'}</span> },
          { key: 'operators', header: t('mfg.wc.operators'), cell: (w) => (
            (w.operator_names || []).length
              ? <div className="flex flex-wrap gap-1" data-testid={`wc-operators-${w.id}`}>{w.operator_names.map((n, i) => <Badge key={`${n}-${i}`} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 font-normal">{n}</Badge>)}</div>
              : <span className="text-zinc-300">—</span>
          ) },
          { key: 'avg_actual', header: t('mfg.wc.avgActual'), align: 'right', cell: (w) => (
            w.avg_actual_hours != null
              ? <span className="tabular-nums text-indigo-600" data-testid={`wc-avg-actual-${w.id}`}>{w.avg_actual_hours} {t('mfg.bom.hoursUnit')} <span className="text-zinc-400 text-[11px]">({w.completed_wo_count})</span></span>
              : <span className="text-zinc-300">—</span>
          ) },
          { key: 'hourly_rate', header: t('mfg.wc.hourlyRate'), align: 'right', cell: (w) => (
            w.hourly_rate != null
              ? <span className="tabular-nums text-zinc-700" data-testid={`wc-rate-${w.id}`}>¥{Number(w.hourly_rate).toLocaleString()}/h</span>
              : <span className="text-zinc-300">—</span>
          ) },
          { key: 'lead', header: t('mfg.wc.lead'), align: 'right', cell: (w) => <span className="tabular-nums text-zinc-600">{w.lead_time_days} {t('mfg.wc.days')}</span> },
          { key: 'active', header: '', cell: (w) => (w.active === false ? <Badge variant="outline" className="bg-zinc-100 text-zinc-500 border-zinc-200">{t('mfg.wc.inactive')}</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t('mfg.wc.activeLabel')}</Badge>) },
          { key: 'actions', actions: true, header: '', cell: (w) => (<>
            {canWrite && <Button size="sm" variant="ghost" data-testid={`wc-edit-${w.id}`} onClick={() => openEdit(w)} className="h-9 px-2 text-zinc-600"><PencilSimple size={16} /></Button>}
            {canDeleteMfg && <Button size="sm" variant="ghost" data-testid={`wc-delete-${w.id}`} onClick={() => del(w)} className="h-9 px-2 text-red-600 hover:bg-red-50"><Trash size={16} /></Button>}
          </>) },
        ]}
      />

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="wc-form-dialog">
          <DialogHeader><DialogTitle>{editId ? t('mfg.edit') : t('mfg.wc.new')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wc.code')} *</Label>
                <Input data-testid="wc-code-input" value={form.code} onChange={set('code')} placeholder="WC-10" className="h-10 mt-1 font-mono" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wc.lead')}</Label>
                <Input data-testid="wc-lead-input" type="number" min="0" value={form.lead_time_days} onChange={set('lead_time_days')} className="h-10 mt-1 text-right" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wc.name')}</Label>
              <Input data-testid="wc-name-input" value={form.name} onChange={set('name')} placeholder="実装 / SMT" className="h-10 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wc.order')}</Label>
                <Input type="number" value={form.sort_order} onChange={set('sort_order')} className="h-10 mt-1 text-right" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wc.hourlyRate')}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">¥</span>
                  <Input data-testid="wc-rate-input" type="number" min="0" step="1" value={form.hourly_rate} onChange={set('hourly_rate')} placeholder="3000" className="h-10 pl-6 text-right" />
                </div></div>
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.notes')}</Label>
              <Input value={form.notes} onChange={set('notes')} className="h-10 mt-1" /></div>
            {/* 担当者 (operators who run this station) */}
            <div data-testid="wc-operators-field">
              <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Users size={14} weight="duotone" className="text-orange-500" /> {t('mfg.wc.operators')}</Label>
              {(form.operator_employee_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {form.operator_employee_ids.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs px-2 py-0.5" data-testid={`wc-op-chip-${id}`}>
                      {peopleName(id)}
                      <button type="button" onClick={() => toggleOperator(id)} className="text-orange-400 hover:text-orange-700"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative mt-1.5">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="wc-op-search" value={opSearch} onChange={(e) => setOpSearch(e.target.value)} placeholder={t('mfg.wc.operatorsSearch')} className="h-9 pl-8 text-sm" />
              </div>
              <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-zinc-200 divide-y divide-zinc-200" data-testid="wc-op-list">
                {filteredPeople.length === 0 ? (
                  <p className="text-xs text-zinc-400 px-3 py-3 text-center">{t('mfg.wc.operatorsEmpty')}</p>
                ) : filteredPeople.slice(0, 100).map((e) => {
                  const checked = (form.operator_employee_ids || []).includes(e.id);
                  return (
                    <button type="button" key={e.id} onClick={() => toggleOperator(e.id)} data-testid={`wc-op-opt-${e.id}`}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${checked ? 'bg-orange-50' : 'hover:bg-zinc-50'}`}>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-orange-500 border-orange-500' : 'border-zinc-300'}`}>
                        {checked && <span className="text-white text-[10px] leading-none">✓</span>}
                      </span>
                      <span className="text-zinc-800">{e.name}</span>
                      {e.department_name && <span className="ml-auto text-[11px] text-zinc-400">{e.department_name}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('mfg.cancel')}</Button>
            <Button data-testid="wc-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.saving') : t('mfg.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
